/* eslint-disable @typescript-eslint/no-unused-vars */
// context/JobContext.tsx
import { STAGE_MAP } from 'constants/jobStage';
import { getEntityId } from 'helpers/entityId';
import { enrichJobWithMetadata, sortJobsByStage } from 'helpers/jobUtils';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { JobWithMetadata, Job } from 'services/api/job';

interface GroupedJobs {
  [key: string]: JobWithMetadata[];
}

interface JobContextType {
  jobs: JobWithMetadata[];
  groupedJobs: GroupedJobs;
  selectedStage: string | null;
  setSelectedStage: (stage: string | null) => void;
  toggleJobPriority: (jobId: string) => void;
  setInitialJobs: (jobs: Job[]) => void;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

export const JobProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [jobs, setJobs] = useState<JobWithMetadata[]>([]);
  const [groupedJobs, setGroupedJobs] = useState<GroupedJobs>({});
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  const setInitialJobs = (initialJobs: Job[]) => {
    const enrichedJobs = initialJobs.map(enrichJobWithMetadata);
    setJobs(enrichedJobs);
  };

  const toggleJobPriority = (jobId: string) => {
    setJobs(prevJobs =>
      prevJobs.map(job =>
        getEntityId(job) === jobId
          ? { ...job, isPriority: !!job.isPriority }
          : job
      )
    );
  };

  useEffect(() => {
    const grouped: GroupedJobs = {
      'new job': [],
      'job staged': [],
      'equip shipped': [],
      'operator dispatched': [],
      'operator returned': [],
      'equip returned': []
    };

    jobs.forEach(job => {
      if (job?.jobStage?.name) {
        let mappedStage =
          STAGE_MAP[job.jobStage.name] || job.jobStage.name.toLowerCase();

        if (mappedStage === 'equipment shipped') mappedStage = 'equip shipped';
        if (mappedStage === 'equipment returned')
          mappedStage = 'equip returned';
        if (mappedStage === 'equip returned') mappedStage = 'equip returned';

        if (grouped[mappedStage]) {
          grouped[mappedStage].push(job);
        }
      }
    });

    Object.keys(grouped).forEach(stage => {
      grouped[stage] = sortJobsByStage(grouped[stage], stage);
    });

    const filteredGroups = Object.fromEntries(
      Object.entries(grouped).filter(([_, jobs]) => jobs.length > 0)
    );

    setGroupedJobs(filteredGroups);
  }, [jobs]);

  return (
    <JobContext.Provider
      value={{
        jobs,
        groupedJobs,
        selectedStage,
        setSelectedStage,
        toggleJobPriority,
        setInitialJobs
      }}
    >
      {children}
    </JobContext.Provider>
  );
};

export const useJobs = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};
